<script>
  import { box, randomBytes } from 'tweetnacl'
  import { encodeBase64 } from 'tweetnacl-util'
  import { settings, contacts, friendGroups, viewStack } from '../../stores'
  import { inboxApi } from '../../api'
  import ViewHeader from '../ViewHeader'
  import ContactSelectionItem from '../ContactSelectionItem.svelte'
  import { PARCEL_TYPES } from '../../constants.js'
  import { packUint8Array } from '../../functions/pack-uint8array.js'
  import { createEncryptedContact } from '../../functions/contact-exchange-coder.js'
  import { contactsSort } from '../../functions/contacts-sort.js'

  let selectedContactA = null
  let isLoading = false
  let errorMessage = null

  const privateKey = $settings.privateKey

  let allContacts = [...$contacts].sort(contactsSort).map(c => ({ contact: c, selected: false }))

  $: matchees = allContacts.filter(c => c.contact.id !== selectedContactA?.id)
  $: selection = matchees.filter(c => c.selected)
  $: allSelected = matchees.length > 0 && selection.length === matchees.length

  function onInviteeChange () {
    // deselect the newly chosen invitee from the matchee list
    for (const c of allContacts) {
      if (c.contact.id === selectedContactA?.id) c.selected = false
    }
  }

  function toggleSelectAll () {
    const target = !allSelected
    for (const c of matchees) c.selected = target
    allContacts = allContacts // trigger reactivity
  }

  function selectGroup (group) {
    for (const c of allContacts) {
      const isMember = group.contactIds.includes(c.contact.id)
      const isAccepting = c.contact.exchangeCompletedAt != null
      const isInvitee = c.contact.id === selectedContactA?.id
      c.selected = isMember && isAccepting && !isInvitee
    }
    allContacts = allContacts // trigger reactivity
  }

  function setErrorMessage (event) {
    errorMessage = event.detail
  }

  async function send () {
    isLoading = true

    try {
      for (const { contact: contactB } of selection) {
        await sendPair(selectedContactA, contactB)
      }
      viewStack.pop()
    } catch {
      alert('An error occurred while trying to send the suggestions.')
    }

    isLoading = false
  }

  async function sendPair (contactA, contactB) {
    const parcelA = contactToParcel(contactA, contactB)
    const parcelB = contactToParcel(contactB, contactA)
    await inboxApi.parcelRequests.upload(parcelA, contactB.inboxServerHost)
    await inboxApi.parcelRequests.upload(parcelB, contactA.inboxServerHost)
  }

  function contactToParcel (contact, recipient) {
    // NOTE: The contact is encrypted with its own secret key (instead of the contacts publicKey)
    // because I wanted to reuse the createEncryptedContact function.
    const { encryptedContact, secretKey } = createEncryptedContact(
      contact.publicKey,
      contact.displayName,
      contact.inboxServerHost
    )

    const nonce = randomBytes(box.nonceLength)
    const encryptedSecretKey = box(secretKey, nonce, recipient.publicKey, privateKey)
    const encryptedSecretKeyAndNonce = packUint8Array(encryptedSecretKey, [nonce])

    const content = { secretKey: encodeBase64(encryptedSecretKeyAndNonce), contact: encryptedContact }

    return {
      recipientContents: [{ recipientId: recipient.id, content: JSON.stringify(content) }],
      type: PARCEL_TYPES.CONTACT
    }
  }
</script>

<ViewHeader>Bring friends together</ViewHeader>

<p>
  Match a friend with one or more others —
  they'll each receive the others' contact details.
</p>

<div class="invitee-row">
  <label for="invitee">Invitee</label>
  <select id="invitee" bind:value={selectedContactA} on:change={onInviteeChange}>
    <option disabled selected value={null}>Select a friend</option>
    {#each allContacts as { contact }}
      {#if contact.exchangeCompletedAt != null}
        <option value={contact}>{contact.displayName}</option>
      {/if}
    {/each}
  </select>
</div>

<div class="list">
  {#if matchees.length > 1}
    <div class="selection-header">
      <label class="select-all">
        <input type="checkbox" checked={allSelected} on:change={toggleSelectAll} />
        <span>Select All</span>
      </label>

      {#if $friendGroups.length > 0}
        <div class="friend-groups-shortcuts">
          {#each $friendGroups as group}
            <button
              class="group-shortcut-btn"
              on:click={() => selectGroup(group)}
              type="button"
            >
              {group.name}
            </button>
          {/each}
        </div>
      {/if}
    </div>
  {/if}

  {#if errorMessage}
    <p class="error">{errorMessage}</p>
  {/if}

  {#each matchees as c (c.contact.id)}
    <ContactSelectionItem
      contact={c.contact}
      bind:selected={c.selected}
      on:errorMessage={setErrorMessage}
    />
  {/each}
</div>

<footer>
  <small>Share personal data responsibly.</small>

  <button
    class="-button-primary"
    disabled={isLoading || !selectedContactA || selection.length === 0}
    on:click={send}
  >
    {#if isLoading}
      Sending...
    {:else if selection.length === 0}
      Select friends to match
    {:else}
      Match {selection.length} friend{selection.length === 1 ? '' : 's'} ✨
    {/if}
  </button>
</footer>

<style>
  .invitee-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0 1rem;
    margin-bottom: 1rem;
  }

  .invitee-row label {
    white-space: nowrap;
    font-weight: bold;
    font-size: 0.9rem;
  }

  .invitee-row select {
    flex: 1;
  }

  .list {
    margin: 0.8rem 1rem 1.5rem 1rem;
  }

  .selection-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    border-bottom: 1px solid var(--light-gray);
    margin-bottom: 0.5rem;
    padding-bottom: 0.25rem;
  }

  .select-all {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0;
    margin-bottom: 0;
  }

  .friend-groups-shortcuts {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    align-items: center;
  }

  .group-shortcut-btn {
    font-size: 0.8rem;
    padding: 0.25rem 0.6rem;
    border: 1px solid var(--brand-color);
    border-radius: 1rem;
    color: var(--brand-color);
    background-color: transparent;
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;
  }

  .group-shortcut-btn:hover {
    background-color: var(--brand-color);
    color: var(--white);
  }

  .error {
    font-size: 0.85rem;
    color: var(--error-color, #c00);
  }

  footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 1rem;
    position: sticky;
    bottom: 0;
    background-color: var(--white);
    border-top: 1px solid var(--light-grey);
  }

  footer small {
    color: var(--dark-grey);
    font-size: 0.8rem;
    max-width: 40%;
  }
</style>
